import { useCallback, useMemo } from "react";
import { styled } from "@/stitches.config";
import { useAppDispatch } from "@/hooks";
import { IWindow, setCurrentExplorer } from "@/store/slices/windows.slice";
import { explorerItemsList } from "@/data/explorer.items.list";
import { Button, Div } from "@/components/base.styled";

export const SearchWrapper = styled("div", {
  fontSize: "$base",
});

export const MenuButton = styled(Button, {
  backgroundColor: "transparent",
  padding: "$sm",
  fontSize: "$sm",
});

interface SearchMenuProps {
  activeWindow: IWindow;
}

export const SearchMenu: React.FC<SearchMenuProps> = ({ activeWindow }) => {
  const dispatch = useAppDispatch();

  const explorerItem = useMemo(() => {
    return explorerItemsList.getItemById(activeWindow.currentExplorer)!;
  }, [activeWindow.currentExplorer]);

  const setCurrentExplorerHandler = useCallback(() => {
    if (explorerItem.parent != null) {
      dispatch(
        setCurrentExplorer({
          parentWindowId: activeWindow.id,
          current: explorerItem.parent,
        })
      );
    }
  }, [activeWindow.id, explorerItem.parent, dispatch]);

  return (
    <Div>
      <MenuButton onClick={setCurrentExplorerHandler}>Zurück</MenuButton>
      <MenuButton>Bearbeiten</MenuButton>
      <MenuButton>Anzeigen</MenuButton>
      <MenuButton css={{ "@bp_400": { display: "none" } }}>Hilfe</MenuButton>
    </Div>
  );
};

export const SearchContentWrapper = styled("div", {
  boxShadow: "$shadow_2",
  backgroundColor: "$mono_white",
  padding: "1rem",
  minHeight: "30rem",
  maxHeight: "60vh",
  overflow: "auto",
});

export const SearchInputWrapper = styled("div", {
  marginBottom: "$md",
});

export const SearchLabel = styled("label", {
  display: "block",
  marginBottom: "$sm",
  fontWeight: "bold",
});

export const SearchInputRow = styled("div", {
  display: "flex",
  gap: "$sm",
  alignItems: "flex-start",
});

export const SearchInputContainer = styled("div", {
  position: "relative",
  flexGrow: 1,
});

export const SearchInput = styled("input", {
  display: "block",
  width: "100%",
  boxShadow: "$shadow_2",
  padding: "$sm",
  border: "none",
  fontSize: "$base",
  "&:focus": {
    outline: "none",
  },
});

export const SearchDropdown = styled("div", {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  backgroundColor: "$mono_white",
  boxShadow: "$shadow_2",
  border: "2px solid $mono_300",
  borderTop: "none",
  maxHeight: "20rem",
  overflowY: "auto",
  zIndex: 1000,
  marginTop: "-2px",
});

export const DropdownItem = styled("div", {
  padding: "$sm",
  cursor: "pointer",
  fontSize: "$base",
  borderBottom: "1px solid $mono_200",
  
  "&:last-child": {
    borderBottom: "none",
  },
  
  "&:hover": {
    backgroundColor: "$mono_100",
  },
  
  "&:active": {
    backgroundColor: "$mono_200",
  },
});

export const HistoryButton = styled("button", {
  padding: "$sm",
  minWidth: "3.5rem",
  height: "3.5rem",
  backgroundColor: "$mono_200",
  border: "2px outset $mono_300",
  fontSize: "1.6rem",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  
  "&:hover": {
    backgroundColor: "$mono_300",
  },
  
  "&:active": {
    border: "2px inset $mono_300",
  },
  
  "&:focus": {
    outline: "1px dotted black",
    outlineOffset: "-4px",
  },
});

export const SearchResultsWrapper = styled("div", {
  marginTop: "$md",
});

export const SearchResultsLabel = styled("div", {
  marginBottom: "$sm",
  fontWeight: "bold",
});

export const SearchResultsList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "$sm",
});

export const SearchResultItem = styled("button", {
  display: "flex",
  alignItems: "center",
  padding: "$sm",
  backgroundColor: "$mono_white",
  border: "1px solid $mono_300",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "$base",
  
  "&:hover": {
    backgroundColor: "$mono_100",
  },
  
  "&:active": {
    backgroundColor: "$mono_200",
  },
});

export const ResultIcon = styled("div", {
  marginRight: "$sm",
  flexShrink: 0,
});

export const NoResultsMessage = styled("div", {
  padding: "$md",
  textAlign: "center",
  color: "$mono_600",
  fontStyle: "italic",
});

export const SearchInfoBox = styled("div", {
  marginTop: "0.4rem",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",

  "& > *": {
    boxShadow: "$shadow_1",
    padding: "0.6rem",
  },
});

